import { useState, useEffect } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { ChartsSection } from "../components/dashboard/ChartsSection";
import { EnrollmentTable } from "../components/dashboard/EnrollmentTable";
import { Button } from "../components/ui/Button";
import { RefreshCw } from "lucide-react";
import { analyticsService } from "../services/analyticsService";
import { useAuth } from "../contexts/AuthContext";

interface DashboardSummary {
  totalUsers: number;
  totalEnrollments: number;
  totalCourses: number;
  totalLessons: number;
  totalInstructors: number;
  totalWatchTime: number;
  userGrowth?: number;
  enrollmentGrowth?: number;
  completionRate?: number;
}

interface RevenueData {
  total: number;
  current: number;
  previous: number;
  growth?: number;
}

interface MonthlyEnrollment {
  _id?: string;
  month?: string;
  count: number;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState("month");
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [revenue, setRevenue] = useState<RevenueData | null>(null);
  const [monthlyEnrollments, setMonthlyEnrollments] = useState<
    MonthlyEnrollment[]
  >([]);

  const fetchDashboardData = async () => {
    setIsLoading(true);
    try {
      const [summaryRes, revenueRes, enrollmentsRes] = await Promise.all([
        analyticsService.getDashboardSummary({ period }),
        analyticsService.getRevenueStatistics({ period }),
        analyticsService.getMonthlyEnrollments({ months: 6 }),
      ]);

      setSummary((summaryRes.summary || summaryRes) as DashboardSummary);
      setRevenue((revenueRes.revenue || revenueRes) as RevenueData);
      setMonthlyEnrollments(
        ((enrollmentsRes as any).enrollments ||
          (enrollmentsRes as any).data ||
          []) as MonthlyEnrollment[],
      );
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [period]);

  const formatTrend = (value?: number) => {
    if (value === undefined || value === null) return "0 %";
    return `${value >= 0 ? "" : ""}${value} %`;
  };

  const formatCurrency = (value: number) => {
    return `${value?.toLocaleString() || 0} USD`;
  };

  const userName = user?.firstName || user?.username || "Admin";

  return (
    <DashboardLayout>
      {/* Welcome Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 sm:mb-8 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl">👋</span>
            <h1 className="text-xl font-semibold text-[#102040]">
              Welcome {userName}
            </h1>
          </div>
          <p className="text-[#989797] text-sm">
            Here's a quick overview of AH Planning
          </p>
        </div>

        <div className="flex items-center gap-2">
          {["week", "month", "quarter", "year"].map((p) => (
            <Button
              key={p}
              variant={period === p ? "primary" : "outline"}
              size="sm"
              onClick={() => setPeriod(p)}
              className="capitalize"
            >
              {p === "week"
                ? "This Week"
                : p === "month"
                  ? "This Month"
                  : p === "quarter"
                    ? "Quarter"
                    : "Year"}
            </Button>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={fetchDashboardData}
            disabled={isLoading}
          >
            <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
          </Button>
        </div>
      </div>

      {/* Analytics */}
      <div className="mb-6 sm:mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4 sm:mb-6">
          Analytics
        </h2>
        <ChartsSection
          monthlyEnrollments={monthlyEnrollments}
          isLoading={isLoading}
        />
      </div>

      {/* Enrollment Table */}
      <div className="mb-6 sm:mb-8">
        <EnrollmentTable />
      </div>
    </DashboardLayout>
  );
}
